/* ==========================================================================
   inMOGnito — theme.js (vanilla ES6, no dependencies)
   Modules: header scroll state, mobile drawer, reveal-on-scroll, marquee,
   hero parallax, product gallery, quantity steppers, accordions,
   AJAX add-to-cart, live cart count, cart page updates, sticky mobile ATC,
   collection sort. Respects prefers-reduced-motion throughout.
   ========================================================================== */
(() => {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const strings = window.themeStrings || {};
  const routes = window.themeRoutes || {
    cart: '/cart',
    cartAdd: '/cart/add',
    cartChange: '/cart/change',
    cartState: '/cart.js'
  };

  const fetchConfig = () => ({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  });

  const formatMoney = (cents) => {
    const currency = document.documentElement.getAttribute('data-currency') || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  /* ------------------------------------------------------------------
     Header scroll state (shrink + shadow)
  ------------------------------------------------------------------ */
  const initHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  };

  /* ------------------------------------------------------------------
     Mobile drawer (Escape closes, focus returns to trigger)
  ------------------------------------------------------------------ */
  const initDrawer = () => {
    const drawer = document.querySelector('[data-drawer]');
    const openButton = document.querySelector('[data-drawer-open]');
    if (!drawer || !openButton) return;

    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openButton.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      const firstLink = drawer.querySelector('.drawer__menu-link, .drawer__close');
      if (firstLink) firstLink.focus();
    };

    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openButton.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      openButton.focus();
    };

    openButton.addEventListener('click', open);
    drawer.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  };

  /* ------------------------------------------------------------------
     Reveal on scroll with stagger inside [data-reveal-group]
  ------------------------------------------------------------------ */
  const initReveal = () => {
    const revealEls = document.querySelectorAll('[data-reveal]');
    if (!revealEls.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      revealEls.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    document.querySelectorAll('[data-reveal-group]').forEach((group) => {
      group.querySelectorAll('[data-reveal]').forEach((el, index) => {
        el.style.setProperty('--reveal-delay', `${Math.min(index * 70, 560)}ms`);
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    revealEls.forEach((el) => observer.observe(el));
  };

  /* ------------------------------------------------------------------
     Marquee: duplicate content so the loop is seamless
  ------------------------------------------------------------------ */
  const initMarquee = () => {
    document.querySelectorAll('[data-marquee]').forEach((track) => {
      const content = track.querySelector('[data-marquee-content]');
      if (!content) return;
      const clone = content.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });
  };

  /* ------------------------------------------------------------------
     Hero parallax (translateY, capped at 6% of viewport scroll)
  ------------------------------------------------------------------ */
  const initParallax = () => {
    if (prefersReducedMotion) return;
    const media = document.querySelector('[data-parallax] img');
    if (!media) return;
    let ticking = false;
    const update = () => {
      const offset = Math.min(window.scrollY * 0.06, window.innerHeight * 0.06);
      media.style.transform = `translateY(${offset}px) scale(1.06)`;
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
  };

  /* ------------------------------------------------------------------
     Product gallery thumbnails
  ------------------------------------------------------------------ */
  const initGallery = () => {
    const gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;
    const main = gallery.querySelector('[data-gallery-main]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    if (!main || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        main.src = thumb.dataset.fullSrc;
        if (thumb.dataset.fullSrcset) main.srcset = thumb.dataset.fullSrcset;
        const thumbImg = thumb.querySelector('img');
        if (thumbImg) main.alt = thumbImg.alt;
        thumbs.forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  };

  /* ------------------------------------------------------------------
     Quantity steppers (product form + cart lines)
  ------------------------------------------------------------------ */
  const initQuantity = () => {
    document.querySelectorAll('[data-quantity]').forEach((wrap) => {
      const input = wrap.querySelector('.quantity__input');
      if (!input) return;
      const step = (delta) => {
        const min = parseInt(input.min, 10) || 0;
        const next = Math.max(min, (parseInt(input.value, 10) || 0) + delta);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      const minus = wrap.querySelector('[data-quantity-minus], [data-cart-qty-minus]');
      const plus = wrap.querySelector('[data-quantity-plus], [data-cart-qty-plus]');
      if (minus) minus.addEventListener('click', () => step(-1));
      if (plus) plus.addEventListener('click', () => step(1));
    });
  };

  /* ------------------------------------------------------------------
     Accordions
  ------------------------------------------------------------------ */
  const initAccordions = () => {
    document.querySelectorAll('[data-accordion]').forEach((accordion) => {
      const trigger = accordion.querySelector('[data-accordion-trigger]');
      const panel = accordion.querySelector('[data-accordion-panel]');
      if (!trigger || !panel) return;
      trigger.addEventListener('click', () => {
        const isOpen = accordion.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', String(isOpen));
        panel.hidden = !isOpen;
      });
    });
  };

  /* ------------------------------------------------------------------
     Cart count bubble (live updates)
  ------------------------------------------------------------------ */
  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((bubble) => {
      bubble.textContent = count;
      bubble.classList.toggle('is-empty', count === 0);
      bubble.classList.add('is-bumped');
      window.setTimeout(() => bubble.classList.remove('is-bumped'), 300);
    });
  };

  const refreshCartCount = async () => {
    try {
      const response = await fetch(routes.cartState, {
        headers: { Accept: 'application/json' }
      });
      const cart = await response.json();
      updateCartCount(cart.item_count);
      return cart;
    } catch (error) {
      return null;
    }
  };

  /* ------------------------------------------------------------------
     AJAX add to cart
  ------------------------------------------------------------------ */
  const addToCart = async (items) => {
    const response = await fetch(`${routes.cartAdd}.js`, {
      ...fetchConfig(),
      body: JSON.stringify({ items })
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || strings.cartError || 'Could not add to cart.');
    }
    return response.json();
  };

  const flashButtonSuccess = (button, labelEl) => {
    const target = labelEl || button;
    const original = target.textContent;
    target.textContent = strings.added || 'Added ✓';
    button.classList.add('is-success');
    window.setTimeout(() => {
      target.textContent = original;
      button.classList.remove('is-success');
    }, 1800);
  };

  const initProductForm = () => {
    const form = document.querySelector('[data-product-form]');
    if (!form) return;
    const button = form.querySelector('[data-atc-button]');
    const label = form.querySelector('[data-atc-label]');
    const errorEl = document.querySelector('[data-atc-error]');
    const variantSelect = form.querySelector('[data-variant-select]');
    const priceWrap = document.querySelector('[data-product-price]');

    if (variantSelect && priceWrap) {
      variantSelect.addEventListener('change', () => {
        const option = variantSelect.selectedOptions[0];
        if (!option) return;
        const current = priceWrap.querySelector('.price__current');
        const compare = priceWrap.querySelector('.price__compare');
        if (current && option.dataset.price) current.textContent = option.dataset.price;
        if (compare) compare.textContent = option.dataset.compare || '';
      });
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!button || button.disabled) return;
      const id = parseInt(new FormData(form).get('id'), 10);
      const quantity = parseInt(new FormData(form).get('quantity'), 10) || 1;
      if (errorEl) errorEl.hidden = true;
      button.disabled = true;
      if (label) label.textContent = strings.adding || 'Adding…';

      try {
        await addToCart([{ id, quantity }]);
        await refreshCartCount();
        if (label) label.textContent = strings.addToCart || 'Add to cart';
        flashButtonSuccess(button, label);
      } catch (error) {
        if (label) label.textContent = strings.addToCart || 'Add to cart';
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
      } finally {
        button.disabled = false;
      }
    });

    // Sticky mobile ATC mirrors the main form
    const stickyBar = document.querySelector('[data-sticky-atc]');
    const stickyButton = document.querySelector('[data-sticky-atc-button]');
    if (stickyBar && button) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            stickyBar.classList.toggle('is-visible', show);
            stickyBar.setAttribute('aria-hidden', String(!show));
          });
        },
        { threshold: 0 }
      );
      observer.observe(button);
    }
    if (stickyButton) {
      stickyButton.addEventListener('click', () => {
        form.requestSubmit ? form.requestSubmit() : form.submit();
      });
    }
  };

  /* ------------------------------------------------------------------
     Quick add buttons on product cards
  ------------------------------------------------------------------ */
  const initQuickAdd = () => {
    document.querySelectorAll('[data-quick-add]').forEach((button) => {
      button.addEventListener('click', async () => {
        const id = parseInt(button.dataset.variantId, 10);
        if (!id || button.disabled) return;
        button.disabled = true;
        try {
          await addToCart([{ id, quantity: 1 }]);
          await refreshCartCount();
          flashButtonSuccess(button, button.querySelector('[data-quick-add-label]'));
        } catch (error) {
          window.location.href = routes.cart;
        } finally {
          button.disabled = false;
        }
      });
    });
  };

  /* ------------------------------------------------------------------
     Cart page: quantity change / remove via /cart/change.js
  ------------------------------------------------------------------ */
  const initCartPage = () => {
    const section = document.querySelector('[data-cart-section]');
    if (!section) return;
    const errorEl = section.querySelector('[data-cart-error]');
    const subtotalEl = section.querySelector('[data-cart-subtotal]');

    const changeLine = async (line, quantity) => {
      const lineEl = section.querySelector(`[data-cart-line][data-line-index="${line}"]`);
      if (lineEl) lineEl.classList.add('is-updating');
      if (errorEl) errorEl.hidden = true;

      try {
        const response = await fetch(`${routes.cartChange}.js`, {
          ...fetchConfig(),
          body: JSON.stringify({ line, quantity })
        });
        if (!response.ok) throw new Error(strings.cartError || 'Cart error');
        const cart = await response.json();
        updateCartCount(cart.item_count);

        if (cart.item_count === 0) {
          window.location.reload();
          return;
        }

        if (quantity === 0 && lineEl) {
          lineEl.remove();
          // Re-index remaining lines to keep /cart/change line numbers valid
          section.querySelectorAll('[data-cart-line]').forEach((el, index) => {
            el.dataset.lineIndex = index + 1;
          });
        } else if (lineEl) {
          const item = cart.items[line - 1];
          const totalEl = lineEl.querySelector('[data-line-total]');
          const input = lineEl.querySelector('[data-cart-qty-input]');
          if (item && totalEl) totalEl.textContent = formatMoney(item.final_line_price);
          if (item && input) input.value = item.quantity;
        }

        if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

        // Free-shipping note
        const note = section.querySelector('[data-shipping-note]');
        const threshold = parseInt(section.dataset.freeShippingThreshold, 10) || 0;
        if (note && threshold > 0 && cart.total_price >= threshold) {
          const text = note.querySelector('.cart-shipping-note__text');
          if (text) text.classList.add('is-unlocked');
        }
      } catch (error) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
      } finally {
        if (lineEl) lineEl.classList.remove('is-updating');
      }
    };

    section.querySelectorAll('[data-cart-line]').forEach((lineEl) => {
      const input = lineEl.querySelector('[data-cart-qty-input]');
      const removeButton = lineEl.querySelector('[data-cart-remove]');
      if (input) {
        input.addEventListener('change', () => {
          const quantity = Math.max(0, parseInt(input.value, 10) || 0);
          changeLine(parseInt(lineEl.dataset.lineIndex, 10), quantity);
        });
      }
      if (removeButton) {
        removeButton.addEventListener('click', () => {
          changeLine(parseInt(lineEl.dataset.lineIndex, 10), 0);
        });
      }
    });
  };

  /* ------------------------------------------------------------------
     Collection sort (URL param navigation)
  ------------------------------------------------------------------ */
  const initSort = () => {
    const select = document.querySelector('[data-sort-select]');
    if (!select) return;
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  };

  /* ------------------------------------------------------------------
     Boot
  ------------------------------------------------------------------ */
  const init = () => {
    initHeader();
    initDrawer();
    initMarquee();
    initReveal();
    initParallax();
    initGallery();
    initQuantity();
    initAccordions();
    initProductForm();
    initQuickAdd();
    initCartPage();
    initSort();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
